import $ from 'jquery';
import { GHP_SESSION_KEY } from '../ghp/constants.js';
import { attachDobField } from '../ghp/dobField.js';

/**
 * GHP Checkout — pre-submit gate.
 *
 * Bundled into the site-wide `main.js` entry; self-guards on
 * `body.ghp-checkout` + `window.GhpCheckoutConfig` so this module is a
 * no-op on every page that isn't the GHP checkout.
 *
 * Flow on the user's first click of "Continue to Payment":
 *
 *   1. Intercept click + form submit, preventDefault.
 *   2. POST the current field values to /wp-json/ghp/v1/checkout/precheck.
 *   3. On { ok: false }               → inline error, button re-enabled.
 *   4. On { ok: true, rateChanged }   → trigger update_checkout (sidebar
 *                                       refreshes with the new rate the
 *                                       precheck endpoint just wrote to the
 *                                       cart), show the blocking "price
 *                                       updated" notice, button re-enabled.
 *      On next click, the cart's primary rate equals the new rate, so the
 *      precheck returns rateChanged:false and the submit proceeds.
 *   5. On { ok: true, !rateChanged }  → gate lifted, re-click the place_order
 *                                       button, WC's own handlers run, order
 *                                       creation proceeds.
 */

const NOTICE_ID = 'ghp-checkout-gate-notice';
const NOTICE_CLASS_BASE = 'ghp-gate-notice';

function initGhpCheckoutGate(config) {
  const form = document.querySelector('form.checkout');
  if (!form) {
    return;
  }

  // Targets both place_order buttons by name; re-queries to survive WC fragment refresh.
  const getPlaceOrderButtons = () =>
    Array.from(form.querySelectorAll('button[name="woocommerce_checkout_place_order"]'));

  const fieldKeys = config.fields || {};
  const reasonFieldMap = {
    age_not_eligible: fieldKeys.dob,
    state_excluded:   fieldKeys.state,
  };

  let bypassGate = false;
  let pending = false;
  let removing = false;

  const handler = (event) => {
    if (bypassGate) {
      return;
    }
    if (pending || removing) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    runPrecheck();
  };

  // Form-level submit survives WC fragment refreshes; capture runs before wc-checkout.js.
  form.addEventListener('submit', handler, { capture: true });

  // Delegated click handler for Back → flip GHP intent to 'family'.
  // Otherwise a user who came via the solo path lands on /ghp-enrollment/
  // which auto-enrolls and bounces them right back to /checkout/.
  form.addEventListener('click', function (event) {
    var target = event.target;
    var backBtn = target && target.closest ? target.closest('.btn-back') : null;
    if (!backBtn) {
      return;
    }
    try {
      var raw = window.sessionStorage.getItem(GHP_SESSION_KEY);
      if (!raw) {
        return;
      }
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || parsed.intent === 'family') {
        return;
      }
      parsed.intent = 'family';
      window.sessionStorage.setItem(GHP_SESSION_KEY, JSON.stringify(parsed));
    } catch (e) { /* private mode / corrupt json */ }
  });

  // Re-validate on field edits (bypassGate may be stale after gateway errors).
  const gateResetKeys = [
    fieldKeys.membership,
    fieldKeys.dob,
    fieldKeys.state,
    fieldKeys.firstName,
    fieldKeys.lastName,
    fieldKeys.email,
    fieldKeys.phone,
    fieldKeys.postcode,
  ];
  const onFieldChange = () => {
    bypassGate = false;
    clearNotice();
  };
  gateResetKeys.forEach((key) => {
    if (!key) {
      return;
    }
    const el = document.getElementById(key) || form.querySelector(`[name="${key}"]`);
    if (!el) {
      return;
    }
    el.addEventListener('input', onFieldChange);
    el.addEventListener('change', onFieldChange);
  });

  // Light input formatters for the two GHP fields.
  const dobInput = document.getElementById(fieldKeys.dob);
  if (dobInput) {
    attachDobField(dobInput);
  }
  const membershipInput = document.getElementById(fieldKeys.membership);
  if (membershipInput) {
    formatMembershipOnInput(membershipInput);
  }

  // Phone is required on GHP — re-validate on blur/change against the same
  // 10-or-11-with-leading-1 rule the server enforces.
  const phoneInput = document.getElementById(fieldKeys.phone);
  if (phoneInput) {
    const validatePhone = () => {
      const wrapper = phoneInput.closest('.form-row');
      if (!wrapper) return;
      const valid = isValidUsPhone(phoneInput.value);
      wrapper.classList.toggle('woocommerce-validated', valid);
      wrapper.classList.toggle('woocommerce-invalid', !valid);
    };
    phoneInput.addEventListener('blur', validatePhone);
    phoneInput.addEventListener('change', validatePhone);
  }

  // Direct trash-button handler: fire AJAX removal immediately, no confirmation modal.
  initDirectRemove();

  function initDirectRemove() {
    const setTrashButtonsDisabled = (disabled) => {
      document.querySelectorAll('.js-ghp-trash-btn').forEach((btn) => {
        if (disabled) {
          btn.setAttribute('disabled', 'disabled');
        } else {
          btn.removeAttribute('disabled');
        }
      });
    };

    document.addEventListener('click', (event) => {
      const btn = event.target && event.target.closest
        ? event.target.closest('.js-ghp-trash-btn')
        : null;
      if (!btn) {
        return;
      }
      if (removing) {
        return;
      }

      const key = btn.getAttribute('data-ghp-cart-item-key') || '';
      if (!key) {
        return;
      }

      removing = true;
      setTrashButtonsDisabled(true);
      disableButton();

      fetch(config.removePersonUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': config.nonce,
        },
        body: JSON.stringify({ cart_item_key: key }),
      })
        .then((response) => response.json().then(
          (data) => ({ status: response.status, data }),
          () => ({ status: response.status, data: null })
        ))
        .then(({ status, data }) => {
          // Treat 404 as success (item already removed by another tab/request).
          const raceResolved = status === 404;
          if (raceResolved || (data && data.ok === true)) {
            enableButton();
            setTrashButtonsDisabled(false);
            $(document.body).trigger('update_checkout');
            removing = false;
            return;
          }
          const msg = (data && data.message)
            || (config.strings && config.strings.removePersonError)
            || 'We could not remove that person. Please try again.';
          showNotice(msg, 'error');
          setTrashButtonsDisabled(false);
          enableButton();
          removing = false;
        })
        .catch((err) => {
          if (window.console && typeof console.error === 'function') {
            console.error('GHP remove-person failed:', err);
          }
          const msg = (config.strings && config.strings.removePersonError)
            || 'We could not remove that person. Please try again.';
          showNotice(msg, 'error');
          setTrashButtonsDisabled(false);
          enableButton();
          removing = false;
        });
    });
  }

  function disableButton() {
    getPlaceOrderButtons().forEach((btn) => {
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('is-loading');
    });
  }

  function enableButton() {
    getPlaceOrderButtons().forEach((btn) => {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-busy');
      btn.classList.remove('is-loading');
    });
  }

  function runPrecheck() {
    clearNotice();

    const membership = readField(fieldKeys.membership);
    const dob        = readField(fieldKeys.dob);
    const state      = readField(fieldKeys.state);

    const s = config.strings || {};
    const email     = readField(fieldKeys.email);
    const phone     = readField(fieldKeys.phone);
    const firstName = readField(fieldKeys.firstName);
    const lastName  = readField(fieldKeys.lastName);
    const postcode  = readField(fieldKeys.postcode);
    const membershipDigits = membership.replace(/\D/g, '');
    const missing = [
      (!membership || membershipDigits.length < 10) && { key: fieldKeys.membership, msg: s.missingMembership || 'EA+ Membership Number must be a 10-digit number.' },
      !email      && { key: fieldKeys.email,      msg: s.missingEmail     || 'Email address is required.' },
      !phone      && { key: fieldKeys.phone,      msg: s.missingPhone     || 'Phone is required.' },
      (phone && !isValidUsPhone(phone)) && { key: fieldKeys.phone, msg: s.invalidPhone || 'Please enter a valid 10-digit US phone number.' },
      !firstName  && { key: fieldKeys.firstName,  msg: s.missingFirstName || 'First name is required.' },
      !lastName   && { key: fieldKeys.lastName,   msg: s.missingLastName  || 'Last name is required.' },
      !dob        && { key: fieldKeys.dob,        msg: s.missingDob       || 'Date of Birth is required.' },
      !state      && { key: fieldKeys.state,      msg: s.missingState     || 'Please select your state before continuing.' },
      !postcode   && { key: fieldKeys.postcode,   msg: s.missingPostcode  || 'ZIP / Postcode is required.' },
    ].find(Boolean);
    if (missing) {
      showNotice(missing.msg, 'error', missing.key);
      const el = missing.key
        ? (document.getElementById(missing.key) || form.querySelector(`[name="${missing.key}"]`))
        : null;
      if (el && typeof el.focus === 'function') {
        el.focus();
      }
      return;
    }

    pending = true;
    disableButton();

    const body = {
      membership_number: membership,
      dob,
      billing_state: state,
    };

    fetch(config.precheckUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': config.nonce,
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.json().then(
        (data) => ({ ok: response.ok, status: response.status, data }),
        () => ({ ok: response.ok, status: response.status, data: null })
      ))
      .then(({ ok, data }) => {
        if (!ok) {
          // Prefer the server's human-readable message (e.g. "Date of Birth is
          // required.") over the generic network fallback used for true
          // transport failures.
          pending = false;
          enableButton();
          const fallback = (config.strings && config.strings.networkError) ||
            'We could not reach the server. Please check your connection and try again.';
          const serverMsg = (data && typeof data.message === 'string' && data.message)
            || fallback;
          showNotice(serverMsg, 'error');
          return;
        }
        handleResponse(data);
      })
      .catch((err) => {
        if (window.console && typeof console.error === 'function') {
          console.error('GHP precheck failed:', err);
        }
        pending = false;
        enableButton();
        const msg = (config.strings && config.strings.networkError) ||
          'We could not reach the server. Please check your connection and try again.';
        showNotice(msg, 'error');
      });
  }

  function handleResponse(data) {
    pending = false;

    if (!data || typeof data !== 'object') {
      if (window.console && typeof console.error === 'function') {
        console.error('GHP precheck: unexpected response shape', data);
      }
      enableButton();
      showNotice('Unexpected response from the server. Please try again.', 'error');
      return;
    }

    // Strict ok===true check; re-enable before showNotice to avoid stuck state.
    if (data.ok !== true) {
      enableButton();
      showNotice(data.message || 'Please correct the errors below and try again.', 'error', reasonFieldMap[data.reason] || null);
      return;
    }

    if (data.rateChanged === true) {
      enableButton();
      expandMobileOrderSummary();
      $(document.body).trigger('update_checkout');
      showNotice(data.message || 'Your rate has been updated based on the date of birth provided.', 'warning');
      return;
    }

    // Gate passed: bypass handler and submit via requestSubmit (survives WC fragment refresh).
    enableButton();
    bypassGate = true;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
    bypassGate = false;
  }

  function readField(name) {
    if (!name) return '';
    const el = document.getElementById(name) || form.querySelector(`[name="${name}"]`);
    return el ? String(el.value || '').trim() : '';
  }

  // Mirrors the server validator: 10 digits, or 11 digits starting with "1".
  function isValidUsPhone(value) {
    let digits = String(value || '').replace(/\D/g, '');
    if (digits.length === 11 && digits[0] === '1') {
      digits = digits.slice(1);
    }
    return /^\d{10}$/.test(digits);
  }

  function expandMobileOrderSummary() {
    const content = document.querySelector('.sh-content');
    const toggle  = document.querySelector('.sh-toggle');
    if (!content || !toggle) {
      return;
    }
    if (!content.classList.contains('visually-hidden-sm')) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    content.classList.remove('visually-hidden-sm');
    const summaryLabel = toggle.querySelector('.summary');
    if (summaryLabel) {
      summaryLabel.textContent = 'Hide order summary';
    }
    const arrowDown = toggle.querySelector('.arrow-down');
    if (arrowDown) {
      arrowDown.classList.replace('arrow-down', 'arrow-up');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function showNotice(message, tone, fieldKey) {
    clearNotice();
    const wrapper = document.createElement('div');
    wrapper.id = NOTICE_ID;
    wrapper.className = `${NOTICE_CLASS_BASE} ${NOTICE_CLASS_BASE}--${tone}`;
    wrapper.setAttribute('role', tone === 'error' ? 'alert' : 'status');
    wrapper.setAttribute('aria-live', 'polite');

    const text = document.createElement('span');
    text.className = `${NOTICE_CLASS_BASE}__text`;
    text.textContent = message;
    wrapper.appendChild(text);

    if (tone === 'warning') {
      const dismiss = document.createElement('button');
      dismiss.type = 'button';
      dismiss.className = `${NOTICE_CLASS_BASE}__close`;
      dismiss.setAttribute('aria-label', 'Dismiss rate update notice');
      dismiss.appendChild(buildCloseIcon());
      dismiss.addEventListener('click', clearNotice);
      wrapper.appendChild(dismiss);
    }

    // Insert the notice directly after the relevant field's row wrapper when
    // a fieldKey is known, otherwise fall back to after the membership row.
    let insertAfter = null;
    if (fieldKey) {
      const el = document.getElementById(fieldKey) || form.querySelector(`[name="${fieldKey}"]`);
      insertAfter = el ? el.closest('.form-row') : null;
    }
    if (!insertAfter) {
      if (tone === 'warning') {
        insertAfter = document.querySelector('.woocommerce-checkout-review-order-table');
      }
      if (!insertAfter) {
        insertAfter = form.querySelector('.ghp-membership-row')
          || form.querySelector('.ghp-help-row')
          || form.querySelector('.ghp-coverage-summary');
      }
    }

    if (!insertAfter) {
      if (window.console && typeof console.warn === 'function') {
        console.warn('GHP showNotice: no anchor found, notice dropped:', message);
      }
      return;
    }

    insertAfter.insertAdjacentElement('afterend', wrapper);
    if (!insertAfter.closest('#order_review')) {
      wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function clearNotice() {
    const existing = document.getElementById(NOTICE_ID);
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
    }
  }
}

const SVG_NS = 'http://www.w3.org/2000/svg';

function buildCloseIcon() {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '2');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('d', 'M4 4l8 8M12 4l-8 8');
  svg.appendChild(path);

  return svg;
}

function formatMembershipOnInput(el) {
  el.addEventListener('input', () => {
    const digits = el.value.replace(/\D/g, '').slice(0, 10);
    if (el.value !== digits) {
      el.value = digits;
    }
  });
}

// Boot once the DOM is ready. `GhpCheckoutConfig` is only localized onto
// GHP checkout page loads, so on every other page this block no-ops.
$(function () {
  const config = window.GhpCheckoutConfig;
  if (!config || !document.body.classList.contains('ghp-checkout')) {
    return;
  }
  initGhpCheckoutGate(config);
});

// Enroll-now / plan-selection page: clear GHP sessionStorage when the user
// clicks "Add to Cart" or "Proceed to cart" so stale See-my-Price / family
// drafts don't carry over into a fresh checkout.
$(function () {
  var targets = document.querySelectorAll('.add-base-product, .js-ghp-clear-on-click');
  if (!targets.length) {
    return;
  }
  var clear = function () {
    try {
      window.sessionStorage.removeItem(GHP_SESSION_KEY);
      window.sessionStorage.removeItem('ghpFamilyDraft');
    } catch (e) {
      console.warn('ghpSeeMyPrice update failed:', e);
    }
  };
  targets.forEach(function (el) {
    el.addEventListener('click', clear);
  });
});
